import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { Components } from 'react-markdown';

import { FormControlLabel, Checkbox as CheckboxMUI, Typography, Box } from '@mui/material';

import { Button } from '../button/Button';
import { Markdown } from '../markdown/Markdown';

import type { TNameType } from '../../types/components/components.type';
import type { ICheckboxProps } from './checkbox.type';

const MARKDOWN_COMPONENTS_CHECKBOX: Components = {
  p: ({ children }) => <Typography component="span">{children}</Typography>,
};

export const Checkbox = <TName extends TNameType>({
  name,
  label,
  value,
  handleChange,
  handleBlur,
  labelLong = '',
  strikeSelected = false,
  disabled = false,
  color = 'default',
  required = false,
  StartIcon,
}: ICheckboxProps<TName>) => {
  const [open, setOpen] = useState<boolean>(false);
  const t = useTranslations();

  return (
    <Box sx={{ display: 'flex' }}>
      {StartIcon && (
        <Box sx={{ mr: 2, pt: 1 }}>
          <StartIcon />
        </Box>
      )}

      <FormControlLabel
        color={color}
        onChange={() => handleChange({ key: name, value: !value })}
        onBlur={handleBlur}
        disabled={disabled}
        control={
          <CheckboxMUI
            slotProps={{ input: { 'aria-label': `${t('labels.select')} ${name}` } }}
            checked={value}
            sx={{
              paddingTop: 0,
            }}
          />
        }
        label={
          <Typography
            component="span"
            sx={strikeSelected && value ? { opacity: '50%', textDecoration: 'line-through' } : {}}
          >
            <Markdown components={MARKDOWN_COMPONENTS_CHECKBOX}>
              {open && labelLong ? labelLong : label}
            </Markdown>

            {required && (
              <Typography component="span" color="error">
                {` (${t('labels.required')})`}
              </Typography>
            )}

            {labelLong && (
              <Button
                handleClick={() => {
                  setOpen(!open);
                }}
                type="button"
                variant="text"
                size="small"
              >
                {open ? t('labels.less') : t('labels.more')}
              </Button>
            )}
          </Typography>
        }
        sx={{
          paddingBottom: '1.2rem',
          alignItems: 'flex-start',
        }}
      />
    </Box>
  );
};
